/**
 * SQLite Database Schema for Offline Support
 * Mirrors the Supabase database structure for local data storage
 */

// Core table creation SQL statements
export const CREATE_TABLES_SQL = {
  // Vehicles table
  vehicles: `
    CREATE TABLE IF NOT EXISTS vehicles (
      id TEXT PRIMARY KEY,
      license_plate TEXT NOT NULL UNIQUE,
      make_model TEXT,
      year INTEGER,
      vin_number TEXT,
      registration_expiry TEXT,
      insurance_expiry TEXT,
      vehicle_type TEXT CHECK (vehicle_type IN ('sedan', 'suv', 'van', 'truck')),
      status TEXT DEFAULT 'available' CHECK (status IN ('available', 'assigned', 'maintenance', 'retired')),
      current_mileage INTEGER DEFAULT 0,
      fuel_type TEXT CHECK (fuel_type IN ('petrol', 'diesel', 'hybrid', 'electric')),
      seating_capacity INTEGER,
      color TEXT,
      owner_name TEXT,
      purchase_date TEXT,
      purchase_price REAL,
      notes TEXT,
      created_at TEXT DEFAULT CURRENT_TIMESTAMP,
      updated_at TEXT DEFAULT CURRENT_TIMESTAMP,
      -- Offline sync fields
      last_synced_at TEXT,
      needs_sync INTEGER DEFAULT 0,
      sync_version INTEGER DEFAULT 1
    );
  `,

  // Drivers table
  drivers: `
    CREATE TABLE IF NOT EXISTS drivers (
      id TEXT PRIMARY KEY,
      name TEXT NOT NULL,
      license_number TEXT,
      license_expiry TEXT,
      phone TEXT,
      email TEXT,
      address TEXT,
      nationality TEXT,
      date_of_birth TEXT,
      hire_date TEXT,
      salary REAL,
      commission_rate REAL,
      status TEXT DEFAULT 'active' CHECK (status IN ('active', 'inactive', 'suspended', 'terminated')),
      emergency_contact_name TEXT,
      emergency_contact_phone TEXT,
      visa_expiry TEXT,
      passport_number TEXT,
      emirates_id TEXT,
      bank_account_number TEXT,
      bank_name TEXT,
      notes TEXT,
      created_at TEXT DEFAULT CURRENT_TIMESTAMP,
      updated_at TEXT DEFAULT CURRENT_TIMESTAMP,
      -- Offline sync fields
      last_synced_at TEXT,
      needs_sync INTEGER DEFAULT 0,
      sync_version INTEGER DEFAULT 1
    );
  `,

  // Vehicle assignments table
  vehicle_assignments: `
    CREATE TABLE IF NOT EXISTS vehicle_assignments (
      id TEXT PRIMARY KEY,
      vehicle_id TEXT NOT NULL,
      primary_driver_id TEXT NOT NULL,
      secondary_driver_id TEXT,
      assignment_date TEXT NOT NULL,
      return_date TEXT,
      daily_rate REAL NOT NULL,
      assignment_type TEXT CHECK (assignment_type IN ('delivery', 'receiving', 'swap')),
      status TEXT DEFAULT 'active' CHECK (status IN ('active', 'completed', 'cancelled')),
      is_shared INTEGER DEFAULT 0,
      notes TEXT,
      created_at TEXT DEFAULT CURRENT_TIMESTAMP,
      updated_at TEXT DEFAULT CURRENT_TIMESTAMP,
      -- Offline sync fields
      last_synced_at TEXT,
      needs_sync INTEGER DEFAULT 0,
      sync_version INTEGER DEFAULT 1,
      FOREIGN KEY (vehicle_id) REFERENCES vehicles(id),
      FOREIGN KEY (primary_driver_id) REFERENCES drivers(id),
      FOREIGN KEY (secondary_driver_id) REFERENCES drivers(id)
    );
  `,

  // Vehicle receiving forms table
  vehicle_receiving_forms: `
    CREATE TABLE IF NOT EXISTS vehicle_receiving_forms (
      id TEXT PRIMARY KEY,
      reference_number TEXT NOT NULL UNIQUE,
      assignment_id TEXT,
      vehicle_id TEXT,
      driver_id TEXT,
      secondary_driver_id TEXT,
      assignment_date TEXT NOT NULL,
      assignment_time TEXT NOT NULL,
      rent_amount REAL NOT NULL,
      current_mileage INTEGER NOT NULL,
      previous_mileage INTEGER,
      exterior_condition TEXT CHECK (exterior_condition IN ('excellent', 'good', 'fair', 'poor')),
      interior_condition TEXT CHECK (interior_condition IN ('excellent', 'good', 'fair', 'poor')),
      tire_condition TEXT CHECK (tire_condition IN ('excellent', 'good', 'fair', 'poor')),
      has_damages INTEGER DEFAULT 0,
      existing_damage_description TEXT,
      has_spare_key INTEGER DEFAULT 1,
      has_documents INTEGER DEFAULT 1,
      has_first_aid_kit INTEGER DEFAULT 1,
      has_fire_extinguisher INTEGER DEFAULT 1,
      has_warning_triangle INTEGER DEFAULT 1,
      has_photos INTEGER DEFAULT 0,
      photo_count INTEGER DEFAULT 0,
      photo_urls TEXT, -- JSON array as text
      receiving_notes TEXT,
      additional_notes TEXT,
      driver_signature TEXT,
      secondary_driver_signature TEXT,
      admin_signature TEXT,
      staff_name TEXT,
      created_at TEXT DEFAULT CURRENT_TIMESTAMP,
      created_by TEXT,
      updated_at TEXT DEFAULT CURRENT_TIMESTAMP,
      -- Offline sync fields
      last_synced_at TEXT,
      needs_sync INTEGER DEFAULT 0,
      sync_version INTEGER DEFAULT 1,
      FOREIGN KEY (vehicle_id) REFERENCES vehicles(id),
      FOREIGN KEY (driver_id) REFERENCES drivers(id),
      FOREIGN KEY (secondary_driver_id) REFERENCES drivers(id),
      FOREIGN KEY (created_by) REFERENCES drivers(id)
    );
  `,

  // Vehicle deliveries table
  vehicle_deliveries: `
    CREATE TABLE IF NOT EXISTS vehicle_deliveries (
      id TEXT PRIMARY KEY,
      assignment_id TEXT,
      driver_id TEXT,
      vehicle_id TEXT,
      delivery_date TEXT NOT NULL,
      delivery_time TEXT NOT NULL,
      final_mileage INTEGER NOT NULL,
      fuel_level INTEGER NOT NULL CHECK (fuel_level >= 0 AND fuel_level <= 100),
      exterior_condition TEXT CHECK (exterior_condition IN ('excellent', 'good', 'fair', 'poor')),
      interior_condition TEXT CHECK (interior_condition IN ('excellent', 'good', 'fair', 'poor')),
      tire_condition TEXT CHECK (tire_condition IN ('excellent', 'good', 'fair', 'poor')),
      has_all_keys INTEGER DEFAULT 1,
      has_all_documents INTEGER DEFAULT 1,
      has_spare_wheel INTEGER DEFAULT 1,
      has_jack_and_tools INTEGER DEFAULT 1,
      has_damages INTEGER DEFAULT 0,
      damage_details TEXT, -- JSON as text
      handed_over_by TEXT NOT NULL,
      received_by TEXT NOT NULL,
      additional_notes TEXT,
      created_at TEXT DEFAULT CURRENT_TIMESTAMP,
      updated_at TEXT DEFAULT CURRENT_TIMESTAMP,
      -- Offline sync fields
      last_synced_at TEXT,
      needs_sync INTEGER DEFAULT 0,
      sync_version INTEGER DEFAULT 1,
      FOREIGN KEY (driver_id) REFERENCES drivers(id),
      FOREIGN KEY (vehicle_id) REFERENCES vehicles(id)
    );
  `,

  // Vehicle damages table
  vehicle_damages: `
    CREATE TABLE IF NOT EXISTS vehicle_damages (
      id TEXT PRIMARY KEY,
      receiving_form_id TEXT,
      delivery_form_id TEXT,
      damage_type TEXT NOT NULL,
      damage_severity TEXT CHECK (damage_severity IN ('minor', 'moderate', 'major')),
      damage_location TEXT,
      damage_description TEXT,
      x_coordinate REAL,
      y_coordinate REAL,
      photo_url TEXT,
      created_at TEXT DEFAULT CURRENT_TIMESTAMP,
      updated_at TEXT DEFAULT CURRENT_TIMESTAMP,
      -- Offline sync fields
      last_synced_at TEXT,
      needs_sync INTEGER DEFAULT 0,
      sync_version INTEGER DEFAULT 1,
      FOREIGN KEY (receiving_form_id) REFERENCES vehicle_receiving_forms(id),
      FOREIGN KEY (delivery_form_id) REFERENCES vehicle_deliveries(id)
    );
  `,

  // Accounting transactions table
  accounting_transactions: `
    CREATE TABLE IF NOT EXISTS accounting_transactions (
      id TEXT PRIMARY KEY,
      driver_id TEXT,
      vehicle_id TEXT,
      type TEXT NOT NULL CHECK (type IN ('earning', 'expense', 'credit', 'debit')),
      category TEXT NOT NULL,
      amount REAL NOT NULL,
      description TEXT,
      transaction_date TEXT NOT NULL,
      status TEXT DEFAULT 'completed' CHECK (status IN ('pending', 'completed', 'cancelled')),
      created_at TEXT DEFAULT CURRENT_TIMESTAMP,
      updated_at TEXT DEFAULT CURRENT_TIMESTAMP,
      import_batch_id TEXT,
      batch_start_date TEXT,
      batch_end_date TEXT,
      source_filename TEXT,
      -- Offline sync fields
      last_synced_at TEXT,
      needs_sync INTEGER DEFAULT 0,
      sync_version INTEGER DEFAULT 1,
      FOREIGN KEY (driver_id) REFERENCES drivers(id),
      FOREIGN KEY (vehicle_id) REFERENCES vehicles(id)
    );
  `,

  // Sync queue table for pending operations
  sync_queue: `
    CREATE TABLE IF NOT EXISTS sync_queue (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      operation_type TEXT NOT NULL CHECK (operation_type IN ('create', 'update', 'delete')),
      table_name TEXT NOT NULL,
      record_id TEXT NOT NULL,
      data TEXT, -- JSON data as text
      created_at TEXT DEFAULT CURRENT_TIMESTAMP,
      retry_count INTEGER DEFAULT 0,
      last_error TEXT,
      priority INTEGER DEFAULT 5, -- 1 = highest, 10 = lowest
      status TEXT DEFAULT 'pending' CHECK (status IN ('pending', 'processing', 'completed', 'failed'))
    );
  `,

  // Offline metadata table
  offline_metadata: `
    CREATE TABLE IF NOT EXISTS offline_metadata (
      key TEXT PRIMARY KEY,
      value TEXT,
      updated_at TEXT DEFAULT CURRENT_TIMESTAMP
    );
  `
};

// Indexes for better performance
export const CREATE_INDEXES_SQL = [
  // Vehicle indexes
  'CREATE INDEX IF NOT EXISTS idx_vehicles_license_plate ON vehicles(license_plate);',
  'CREATE INDEX IF NOT EXISTS idx_vehicles_status ON vehicles(status);',
  'CREATE INDEX IF NOT EXISTS idx_vehicles_needs_sync ON vehicles(needs_sync);',

  // Driver indexes  
  'CREATE INDEX IF NOT EXISTS idx_drivers_name ON drivers(name);',
  'CREATE INDEX IF NOT EXISTS idx_drivers_phone ON drivers(phone);',
  'CREATE INDEX IF NOT EXISTS idx_drivers_status ON drivers(status);',
  'CREATE INDEX IF NOT EXISTS idx_drivers_needs_sync ON drivers(needs_sync);',

  // Assignment indexes
  'CREATE INDEX IF NOT EXISTS idx_assignments_vehicle_id ON vehicle_assignments(vehicle_id);',
  'CREATE INDEX IF NOT EXISTS idx_assignments_driver_id ON vehicle_assignments(primary_driver_id);',
  'CREATE INDEX IF NOT EXISTS idx_assignments_status ON vehicle_assignments(status);',
  'CREATE INDEX IF NOT EXISTS idx_assignments_date ON vehicle_assignments(assignment_date);',
  'CREATE INDEX IF NOT EXISTS idx_assignments_needs_sync ON vehicle_assignments(needs_sync);',

  // Form indexes
  'CREATE INDEX IF NOT EXISTS idx_receiving_forms_vehicle_id ON vehicle_receiving_forms(vehicle_id);',
  'CREATE INDEX IF NOT EXISTS idx_receiving_forms_driver_id ON vehicle_receiving_forms(driver_id);',
  'CREATE INDEX IF NOT EXISTS idx_receiving_forms_reference ON vehicle_receiving_forms(reference_number);',
  'CREATE INDEX IF NOT EXISTS idx_receiving_forms_needs_sync ON vehicle_receiving_forms(needs_sync);',

  // Delivery indexes
  'CREATE INDEX IF NOT EXISTS idx_deliveries_vehicle_id ON vehicle_deliveries(vehicle_id);',
  'CREATE INDEX IF NOT EXISTS idx_deliveries_driver_id ON vehicle_deliveries(driver_id);',
  'CREATE INDEX IF NOT EXISTS idx_deliveries_date ON vehicle_deliveries(delivery_date);',
  'CREATE INDEX IF NOT EXISTS idx_deliveries_needs_sync ON vehicle_deliveries(needs_sync);',

  // Damage indexes
  'CREATE INDEX IF NOT EXISTS idx_damages_receiving_form ON vehicle_damages(receiving_form_id);',
  'CREATE INDEX IF NOT EXISTS idx_damages_delivery_form ON vehicle_damages(delivery_form_id);',
  'CREATE INDEX IF NOT EXISTS idx_damages_coordinates ON vehicle_damages(x_coordinate, y_coordinate);',
  'CREATE INDEX IF NOT EXISTS idx_damages_needs_sync ON vehicle_damages(needs_sync);',

  // Accounting indexes
  'CREATE INDEX IF NOT EXISTS idx_accounting_driver_id ON accounting_transactions(driver_id);',
  'CREATE INDEX IF NOT EXISTS idx_accounting_vehicle_id ON accounting_transactions(vehicle_id);',
  'CREATE INDEX IF NOT EXISTS idx_accounting_date ON accounting_transactions(transaction_date);',
  'CREATE INDEX IF NOT EXISTS idx_accounting_type ON accounting_transactions(type);',
  'CREATE INDEX IF NOT EXISTS idx_accounting_needs_sync ON accounting_transactions(needs_sync);',

  // Sync queue indexes
  'CREATE INDEX IF NOT EXISTS idx_sync_queue_status ON sync_queue(status);',
  'CREATE INDEX IF NOT EXISTS idx_sync_queue_priority ON sync_queue(priority);',
  'CREATE INDEX IF NOT EXISTS idx_sync_queue_table ON sync_queue(table_name);',
  'CREATE INDEX IF NOT EXISTS idx_sync_queue_created ON sync_queue(created_at);'
];

// Initial data and metadata
export const INITIAL_METADATA = [
  { key: 'database_version', value: '1.0.0' },
  { key: 'last_full_sync', value: null },
  { key: 'sync_enabled', value: 'true' },
  { key: 'total_records_synced', value: '0' }
];

// Database migration scripts (for future schema updates)
export const MIGRATIONS = {
  '1.0.0': {
    up: Object.values(CREATE_TABLES_SQL).concat(CREATE_INDEXES_SQL),
    down: [
      'DROP TABLE IF EXISTS sync_queue;',
      'DROP TABLE IF EXISTS offline_metadata;',
      'DROP TABLE IF EXISTS vehicle_damages;',
      'DROP TABLE IF EXISTS vehicle_deliveries;', 
      'DROP TABLE IF EXISTS vehicle_receiving_forms;',
      'DROP TABLE IF EXISTS accounting_transactions;',
      'DROP TABLE IF EXISTS vehicle_assignments;',
      'DROP TABLE IF EXISTS drivers;',
      'DROP TABLE IF EXISTS vehicles;'
    ]
  }
};